import React, { useState } from 'react';
import styled from 'styled-components';
import { Building, Plus, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Facility } from '../../types/facility';
import FacilitiesList from './FacilitiesList';
import FacilityForm from './FacilityForm';
import FacilityDetails from './FacilityDetails';
import { SkeletonFacilityManagement } from '../Skeleton/SkeletonLoader';

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }
        `;
    }
  }}
`;

const FacilityManagement: React.FC = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);

  const handleAddFacility = () => {
    setEditingFacility(null);
    setShowForm(true);
  };

  const handleEditFacility = (facility: Facility) => {
    setEditingFacility(facility);
    setShowForm(true);
    setShowDetails(false);
  };

  const handleViewFacility = (facilityId: string) => {
    // In a real implementation, you would fetch the facility by ID
    // For now, we'll just show the form with the facility data
    setSelectedFacility(editingFacility);
    setShowDetails(true);
    setShowForm(false);
  };

  const handleDeleteFacility = (facilityId: string) => {
    // Handle facility deletion
    console.log('Delete facility:', facilityId);
    // Refresh the list after deletion
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingFacility(null);
    // Refresh the facilities list
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingFacility(null);
  };

  const handleDetailsClose = () => {
    setShowDetails(false);
    setSelectedFacility(null);
  };

  const handleViewStats = () => {
    navigate('/facilities/stats');
  };

  return (
    <Container>
      <Header>
        <Title>
          <Building size={32} />
          Facility Management
        </Title>
        <Actions>
          <Button onClick={handleViewStats}>
            <BarChart3 size={20} />
            View Statistics
          </Button>
          <Button variant="primary" onClick={handleAddFacility}>
            <Plus size={20} />
            Add Facility
          </Button>
        </Actions>
      </Header>

      <FacilitiesList />

      {showForm && (
        <FacilityForm
          facility={editingFacility || undefined}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {showDetails && selectedFacility && (
        <FacilityDetails
          facilityId={selectedFacility.id}
          onClose={handleDetailsClose}
          onEdit={handleEditFacility}
          onDelete={handleDeleteFacility}
        />
      )}
    </Container>
  );
};

export default FacilityManagement;
